import { EField as EEpcField } from "@rentiohq/shared-frontend/dist/forms/epc/schema.epcInfo.types";
import {
  IValues,
  mapNullableBooleanValueToFormField,
} from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import {
  AMENITY_NAMES,
  getAmenityBooleanValue,
  getAmenityNumberValue,
  getAmenityStringValue,
  truncateEAN,
} from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity";
import {
  EPropertyTypeId,
  IProperty,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { EAN_LENGTH, EAN_PREFIX } from "./schema.editPropertyExtraInfo.config";
import { EField, IExtraData } from "./schema.editPropertyExtraInfo.types";

export const validationErrorsForEan = (ean = "") => {
  const result: string[] = [];

  if (ean === null || ean === "" || ean === EAN_PREFIX) {
    return result;
  }

  const lengthWithoutPrefix = EAN_LENGTH - EAN_PREFIX.length;

  if (ean.length > lengthWithoutPrefix) {
    result.push(
      getLocalizedText("system.form.error.text.exact_length", {
        value: `${EAN_LENGTH}`,
      }),
    );
  }

  if (ean.length < lengthWithoutPrefix) {
    result.push(
      getLocalizedText("system.form.error.text.exact_length", {
        value: `${EAN_LENGTH}`,
      }),
    );
  }

  return result;
};

export const getInitialValues = ({
  property,
  propertyAmenities,
}: IExtraData): IValues => {
  return {
    // General
    [EField.ConstructionYear]: property.constructionYear,

    // EPC Fields
    [EEpcField.Label]: property.epcLabel,
    [EEpcField.Value]: property.epcValue,
    [EEpcField.ExpirationDate]: property.epcExpirationDate,
    [EEpcField.CertificateId]: property.epcCertificateId,

    // Bookkeeping
    [EField.CostUnit]: property.costUnit,
    [EField.BookkeepingPaidOutStartDate]: property.bookkeepingPaidOutStartDate,

    // Bookkeeping Group
    [EField.CostCenter]: property.costCenter,

    // Electricity
    [EField.ElectricityType]: property.electricityType,
    [EField.ElectricityEan]: truncateEAN(property.electricityEan),
    [EField.ElectricityMeterNumberBoth]: property.electricityMeterNumberBoth,
    [EField.ElectricityMeterType]: property.electricityMeterType,

    // Natural Gas
    [EField.NaturalGasAvailable]: property.naturalGasAvailable,
    [EField.NaturalGasEan]: truncateEAN(property.naturalGasEan),
    [EField.NaturalGasMeterNumber]: property.naturalGasMeterNumber,
    [EField.NaturalGasMeterType]: property.naturalGasMeterType,

    // Water
    [EField.WaterMeterNumber]: property.waterMeterNumber,

    // Exterior - from propertyAmenities
    [EField.BuildingAdjoined]: getAmenityStringValue(
      propertyAmenities,
      AMENITY_NAMES.buildingAdjoined,
    ),
    [EField.BuildingSurfaceArea]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.buildingSurfaceArea,
    ),
    [EField.BuildingGardenSurfaceArea]: property.buildingGardenSurfaceArea,
    [EField.BuildingTerraceSurfaceArea]: property.buildingTerraceSurfaceArea,

    // Interior - from propertyAmenities
    [EField.BuildingFloor]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.buildingFloor,
    ),
    [EField.BuildingFloorCount]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.buildingFloorCount,
    ),
    [EField.RoomCountBedroom]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.roomCountBedroom,
    ),
    [EField.RoomCountBathroom]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.roomCountBathroom,
    ),
    [EField.RoomCountGarage]: getAmenityNumberValue(
      propertyAmenities,
      AMENITY_NAMES.roomCountGarage,
    ),
    [EField.BuildingHasStorage]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(
        propertyAmenities,
        AMENITY_NAMES.buildingHasStorage,
      ),
    ),
    [EField.IsFurnished]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.isFurnished),
    ),

    // Amenities - from propertyAmenities
    [EField.HasOven]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasOven),
    ),
    [EField.HasFridge]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasFridge),
    ),
    [EField.HasDishWasher]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasDishwasher),
    ),
    [EField.HasRangeHood]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasRangeHood),
    ),
    [EField.HasLift]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasLift),
    ),

    // Energy - from propertyAmenities
    [EField.HasDoubleGlazing]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasDoubleGlazing),
    ),
    [EField.HasCentralHeating]: mapNullableBooleanValueToFormField(
      getAmenityBooleanValue(
        propertyAmenities,
        AMENITY_NAMES.hasCentralHeating,
      ),
    ),
    [EField.HeatingType]: getAmenityStringValue(
      propertyAmenities,
      AMENITY_NAMES.heatingType,
    ),
  };
};

export const propertyTypeCanHaveFloors = (params: { property: IProperty }) => {
  const { property } = params;
  const { typeId } = property;

  switch (typeId) {
    case EPropertyTypeId.Apartment:
    case EPropertyTypeId.StudentResidence:
    case EPropertyTypeId.Commercial:
    case EPropertyTypeId.ParkingLot:
    case EPropertyTypeId.Garage:
    case EPropertyTypeId.Office:
      return true;

    case EPropertyTypeId.Group:
    case EPropertyTypeId.House:
    case EPropertyTypeId.Warehouse:
    case EPropertyTypeId.Shed:
    case EPropertyTypeId.Storage:
      return false;
  }
};

export const propertyTypeCanHaveAdjoinedType = (params: {
  property: IProperty;
}) => {
  const { property } = params;
  const { typeId } = property;

  switch (typeId) {
    case EPropertyTypeId.House:
    case EPropertyTypeId.Commercial:
    case EPropertyTypeId.Garage:
    case EPropertyTypeId.Office:
    case EPropertyTypeId.Warehouse:
    case EPropertyTypeId.Shed:
    case EPropertyTypeId.Storage:
      return true;

    case EPropertyTypeId.Group:
    case EPropertyTypeId.Apartment:
    case EPropertyTypeId.StudentResidence:
    case EPropertyTypeId.ParkingLot:
      return false;
  }
};
